import { solid } from '@fortawesome/fontawesome-svg-core/import.macro';
import { Helmet } from "react-helmet";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useCallback, useEffect, useState } from 'react'
import { Alert, Container, Modal, Button, Card, Breadcrumb, Row, Col, Badge, Table } from 'react-bootstrap'
import { Link, useNavigate, useParams } from 'react-router-dom';
import AppLoader from '../../components/common/AppLoader';
import { useFullScreenLoader } from '../../contexts/fullScreenLoader';
import { useToasts } from '../../contexts/toasts';
import { formatDate, getConnectionDetails, createQueryDetails, setObjectType } from '../../utils';
import { Datasets } from '../../api/Datasets';
import UserItem from '../../components/common/UserItem';
import AssetRibbon from '../../components/common/AssetRibbon';
import { VizClient } from '../../api/VizClient';
import { BdaDataset } from '../../models/BdaDataset';
import { Tooltip } from 'antd';
import { DatabaseDatasource, ObjectStorageDatasource } from '../../models/Datasource';
import { DataspaceConnector } from '../../models/DataSpace';
import { TailSpin } from 'react-loader-spinner';
import DataspaceConnectorItem from '../../components/list-items/DataspaceConnectorItem';
import { DataRetriever } from '../../api/DataRetriever';
import AssetProjectsEditor from '../../components/forms/AssetProjectsEditor';

export default function DatasetDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [datasetPreview, setDatasetPreview] = useState<any[]>();
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [dataset, setDataset] = useState<BdaDataset>();
  const [connectors, setConnectors] = useState<DataspaceConnector[]>();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [error, setError] = useState(false);
  const [showProjectsModal, setShowProjectsModal] = useState(false);

  const { showToast } = useToasts();
  const { showLoader, hideLoader } = useFullScreenLoader();

  const initConnectors = useCallback(() => {
    const alidaUrl = window.location.href;

    DataRetriever.getDataspaceConnectorsByUrl(alidaUrl)
      .then(
        ({ data }) => setConnectors(data),
        (err) => {
          setConnectors([]);
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('connectors-error', message, `Model Connectors Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
  }, [showToast]);

  const initDataset = useCallback(() => {
    Datasets.getDataset(+id!)
      .then(
        ({ data: dataset }) => {
          setDataset(dataset);
          initConnectors();
        },
        () => {
          showToast('dataset-error', 'Could not get dataset.', 'Dataset Error', 'danger');
          setError(true);
        }
      );
  }, [id, initConnectors, showToast]);

  useEffect(() => initDataset(), [initDataset]);

  const onEditDataset = useCallback(() => navigate(`/datasets/${id}/edit`), [id, navigate]);
  const onDeleteDataset = useCallback(() => setShowDeleteModal(true), []);
  const handleDeleteClose = useCallback(() => setShowDeleteModal(false), []);

  const onDatasetCopy = useCallback(() => {
    navigator.clipboard.writeText(JSON.stringify(dataset, null, 2));
    showToast("dataset-copy", "Dataset copied to clipboard");
  }, [dataset, showToast]);

  const handlePreviewClose = () => {
    setShowPreviewModal(false);
    setDatasetPreview(undefined);
  };

  const deleteDataset = () => {
    showLoader();

    Datasets.deleteDataset(dataset!.id!)
      .then(
        () => {
          showToast('dataset-delete-success', 'Dataset deleted successfully.', 'Success', 'success');
          hideLoader();
          handleDeleteClose();
          navigate('/datasets');
        },
        () => {
          showToast('dataset-delete-error', 'Could not delete dataset.', 'Dataset Error', 'danger');
          hideLoader();
        }
      );
  };

  const openDatasetPreview = () => {
    setShowPreviewModal(true);

    const connectionDetails = getConnectionDetails(dataset!.datasource!, undefined, dataset!.datasetFileType);
    const body = createQueryDetails(connectionDetails);
    VizClient.getDatasetPreview(body, connectionDetails.type)
      .then(
        ({ data: preview }) => setDatasetPreview(preview?.result?.data || []),
        (err) => {
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('dataset-error', message, `Dataset Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
  }

  const openProjectsModal = () => setShowProjectsModal(true);
  const handleProjectsModalClose = () => setShowProjectsModal(false);

  const renderPreview = () => {
    if (!datasetPreview) return (
      <div className="d-flex justify-content-center py-3">
        <AppLoader size={250} />
      </div>
    )

    return datasetPreview.length > 0
      ? (
        <div className="table-wrapper">
          <Table className="mb-0" bordered>
            <thead>
              <tr>
                {Object.keys(datasetPreview[0]).map(key => (
                  <th key={key}>{key}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {datasetPreview.map((row, i) => (
                <tr key={i}>
                  {Object.keys(row).map((col, j) => (
                    <td key={j}>{row[col]}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )
      : (
        <Alert className="p-2 mb-0">
          <FontAwesomeIcon icon={solid('info-circle')} />
          <span className="ms-2">Dataset is empty.</span>
        </Alert>
      )
  }

  const handlePublishModelClick = useCallback(() => {
    navigate('publish');
  }, [navigate]);

  const renderAsyncContent = () => {
    if (error) return (
      <Alert variant="danger">
        <FontAwesomeIcon icon={solid('exclamation-triangle')} />
        <span className="ms-2">An error occured during dataset retrieve. Try again later.</span>
      </Alert>
    );

    if (!dataset) return (
      <div className="d-flex justify-content-center py-5">
        <AppLoader size={300} />
      </div>
    );

    return (
      <>
        <Breadcrumb>
          <Breadcrumb.Item linkAs={Link} linkProps={{ to: '/datasets' }}>Datasets</Breadcrumb.Item>
          <Breadcrumb.Item active>{dataset.name}</Breadcrumb.Item>
        </Breadcrumb>

        <Card className="item-detail-group main bg-dataset">
          <div className="item-ribbons">
            {(dataset.createdByServiceId === null) && (
              <AssetRibbon
                icon={solid('user')}
                color="info"
                tooltip="This has been created by a user."
              />
            )}
            {(dataset.createdByServiceId !== null) && (
              <AssetRibbon
                icon={solid('cogs')}
                color="dark"
                tooltip="This is an output from an execution."
              />
            )}
            {!dataset.created && (
              <AssetRibbon
                icon={solid('lock')}
                color="warning"
                tooltip="This has not been created yet."
              />
            )}
          </div>

          <FontAwesomeIcon className="item-icon" icon={solid('database')} />

          <Row className="gx-0 justify-content-end">
            <Badge className="w-auto shadow">
              {dataset.accessLevel}
            </Badge>
          </Row>

          <div className="p-2 py-4">
            <Row className="gx-0 align-items-center">
              <Col className="pe-3">
                <h3 className="mb-0">{dataset.name}</h3>
                <p className="text-trailing mb-3">{dataset.description}</p>
              </Col>

              <div className="w-auto">
                <Tooltip title="Manage Projects">
                  <button className="circle-button p-4" onClick={openProjectsModal}>
                    <span className="fa-layers">
                      <FontAwesomeIcon icon={solid('folder')} transform="left-14 grow-2" />
                      <FontAwesomeIcon icon={solid('cog')} transform="shrink-4 down-8 right-3" />
                    </span>
                  </button>
                </Tooltip>
                <Tooltip title="Publish">
                  <button className="circle-button p-4 ms-3" onClick={handlePublishModelClick}>
                    <FontAwesomeIcon icon={solid('share-from-square')} />
                  </button>
                </Tooltip>
                <Tooltip title="Copy to Clipboard">
                  <button className="circle-button p-4 ms-3" onClick={onDatasetCopy}>
                    <FontAwesomeIcon icon={solid('clipboard')} />
                  </button>
                </Tooltip>
                {dataset.datasetFileType!.type === "table" && (
                  <Tooltip title="Preview">
                    <button className="circle-button p-4 ms-3" disabled={!dataset.created} onClick={openDatasetPreview}>
                      <FontAwesomeIcon icon={solid('eye')} />
                    </button>
                  </Tooltip>
                )}
                <Tooltip title="Edit">
                  <button className="circle-button p-4 ms-3" disabled={!dataset.created} onClick={onEditDataset}>
                    <FontAwesomeIcon icon={solid('pen')} />
                  </button>
                </Tooltip>
                <Tooltip title="Delete">
                  <button className="circle-button p-4 ms-3" disabled={!dataset.created} onClick={onDeleteDataset}>
                    <FontAwesomeIcon icon={solid('trash')} />
                  </button>
                </Tooltip>
              </div>
            </Row>
          </div>

          <Row className="gx-0 justify-content-between">
            <Col xs="auto">
              <div className="bg-highlight">
                <UserItem className="negative" user={dataset.owner!} organization={dataset.organization!} />
              </div>
            </Col>

            <div className="d-flex flex-wrap">
              {
                dataset.tags.map((tag, index) => <Badge className="m-1" pill={true} key={index}>{tag}</Badge>)
              }
            </div>
          </Row>

          <div className="item-dates">
            <div className="d-inline-block">
              <b>Created:</b>
              <span className="ms-1">{formatDate(dataset.createDateTime!)}</span>
            </div>
            <div className="d-inline-block ms-2">
              <b>Last Update:</b>
              <span className="ms-1">{formatDate(dataset.updateDateTime!)}</span>
            </div>
          </div>
        </Card>

        <Card className="item-detail-group mb-3">
          <div className="item-detail-highlight">
            <FontAwesomeIcon icon={solid('database')} />
            <h5 className="d-inline ms-2 mb-0">Dataset Source</h5>
          </div>

          <Card.Body>
            <Table className="mb-0">
              <tbody>
                <tr>
                  <th scope="col">Name</th>
                  <td className="text-end">
                    <Link to={`/datasources/${dataset.datasource!.id}`} target="_blank" rel="noopener noreferrer">{dataset.datasource!.name}</Link>
                  </td>
                </tr>
                <tr>
                  <th scope="col">Description</th>
                  <td className="text-end">{dataset.datasource!.description}</td>
                </tr>
                <tr>
                  <th scope="col">Driver</th>
                  <td className="text-end">{dataset.datasource!.driverType}</td>
                </tr>
                <tr>
                  <th scope="col">Host</th>
                  <td className="text-end">{dataset.datasource!.host}</td>
                </tr>
                <tr>
                  <th scope="col">Port</th>
                  <td className="text-end">{dataset.datasource!.port}</td>
                </tr>
                {dataset.datasource!.type === "objectStorage" && setObjectType<ObjectStorageDatasource>(dataset.datasource) && (
                  <tr>
                    <th scope="col">Bucket</th>
                    <td className="text-end">
                      <FontAwesomeIcon icon={solid('bucket')} size="sm" />
                      <span className="ms-2">{dataset.datasource!.bucket}</span>
                    </td>
                  </tr>
                )}
                {dataset.datasource!.type === "database" && setObjectType<DatabaseDatasource>(dataset.datasource) && (
                  <tr>
                    <th scope="col">Database</th>
                    <td className="text-end">
                      <FontAwesomeIcon icon={solid('database')} size="sm" />
                      <span className="ms-2">{dataset.datasource!.database}</span>
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </Card.Body>
        </Card>

        <Card className="item-detail-group mb-3">
          <div className="item-detail-highlight">
            <FontAwesomeIcon icon={solid('info-circle')} />
            <h5 className="d-inline ms-2 mb-0">Dataset Info</h5>
          </div>

          <Card.Body>
            <Table className="mb-0">
              <tbody>
                {dataset.datasetFileType!.tableName && (
                  <tr>
                    <th scope="col">Table Name</th>
                    <td className="text-end">{dataset.datasetFileType!.tableName}</td>
                  </tr>
                )}
                {dataset.datasetFileType!.path && (
                  <tr>
                    <th scope="col">Path</th>
                    <td className="text-end">{dataset.datasetFileType!.path}</td>
                  </tr>
                )}
                <tr>
                  <th scope="col">Type</th>
                  <td className="text-end">{dataset.datasetFileType!.type}</td>
                </tr>
                {dataset.createdByBdaId && (
                  <tr>
                    <th className="align-middle" scope="col">Created by Application</th>
                    <td className="text-end">
                      <Link className="d-inline-block" to={`/bda-applications/${dataset.createdByBdaId}`} target="_blank" rel="noopener noreferer">
                        <div className="border bg-light px-2 rounded">
                          <FontAwesomeIcon icon={solid('laptop-code')} />
                          <span className="ms-2">{dataset.createdByBdaId}</span>
                        </div>
                      </Link>
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
            {dataset.datasetFileType!.columns && (
              <>
                <h6 className="mt-3">Dataset Columns</h6>
                <Table className="mb-0">
                  <tbody>
                    {dataset.datasetFileType!.columns.map(column => (
                      <tr key={column.name}>
                        <th scope="col">{column.name}</th>
                        <td className="text-end">
                          <code>{column.type}</code>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </>
            )}
          </Card.Body>
        </Card>

        <Card className="item-detail-group">
          <div className="item-detail-highlight">
            <FontAwesomeIcon icon={solid('store')} fixedWidth />
            <h5 className="d-inline ms-2 mb-0">Available at</h5>
          </div>

          <Card.Body className="p-0 pt-1 px-2">
            {!connectors && (
              <div className="d-flex justify-content-center py-3">
                <TailSpin width={60} height={60} color="#677ab1" />
              </div>
            )}

            {connectors?.length === 0
              ? (
                <Alert variant="info" className="mt-2 p-2">
                  <FontAwesomeIcon icon={solid('info-circle')} />
                  <span className="ms-2">No offers found for this model.</span>
                </Alert>
              )
              : (
                <Row>
                  {connectors?.map((connector) => (
                    <Col key={connector.id} className="p-2" md={6} lg={4}>
                      <DataspaceConnectorItem className="h-100" connector={connector} actions={<></>} />
                    </Col>
                  ))}
                </Row>
              )
            }
          </Card.Body>
        </Card>
      </>
    );
  }

  return (
    <Container className="mt-3">
      <Helmet>
        <title>Dataset {id} Detail</title>
      </Helmet>

      {renderAsyncContent()}

      <br />

      <Modal show={showDeleteModal} onHide={handleDeleteClose}>
        <Modal.Header closeButton>
          <Modal.Title>Delete Dataset</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to delete dataset <b>{dataset && dataset.name}</b>?</p>

          <Alert className="p-2" variant="warning">
            <FontAwesomeIcon icon={solid('exclamation-triangle')} />
            <span className="ms-2">Remember, this operation cannot be undone.</span>
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" size="sm" className="border" onClick={handleDeleteClose}>Close</Button>
          <Button variant="danger" size="sm" onClick={deleteDataset}>Delete</Button>
        </Modal.Footer>
      </Modal>

      <Modal size="lg" show={showPreviewModal} onHide={handlePreviewClose}>
        <Modal.Header closeButton>
          <Modal.Title>Dataset Preview</Modal.Title>
        </Modal.Header>
        <Modal.Body>{renderPreview()}</Modal.Body>
        <Modal.Footer>
          <Button variant="light" size="sm" className="border" onClick={handlePreviewClose}>Close</Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showProjectsModal} onHide={handleProjectsModalClose}>
        <Modal.Header closeButton>
          <Modal.Title>Dataset Projects</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <AssetProjectsEditor assetId={+id!} assetType="dataset" assetLabel="Dataset" />
        </Modal.Body>
        <Modal.Footer className="justify-content-center">
          <Button variant="light" className="border" onClick={handleProjectsModalClose} size="sm">Cancel</Button>
        </Modal.Footer>
      </Modal>
    </Container>
  )
}
